import { useEffect, useRef, type RefObject } from "react";
import * as THREE from "three";

export type Pano360WebGLVideoProps = {
  videoRef: RefObject<HTMLVideoElement | null>;
  enabled?: boolean;
  /** Vertical field of view in degrees; lower = zoomed in (default 75). */
  fovDeg?: number;
};

/**
 * Immersive 360° view driven by an existing {@link HTMLVideoElement}
 * (flashback playback / live relay). Drag to look; scroll wheel to tilt.
 */
const DEFAULT_FOV_DEG = 75;

export function Pano360WebGLVideo({
  videoRef,
  enabled = true,
  fovDeg = DEFAULT_FOV_DEG,
}: Pano360WebGLVideoProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const fovTargetRef = useRef(fovDeg);
  fovTargetRef.current = fovDeg;

  useEffect(() => {
    const video = videoRef.current;
    if (!enabled || !video) return;
    const host = hostRef.current;
    if (!host) return;

    let destroyed = false;
    let raf = 0;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(host.clientWidth, host.clientHeight, false);
    renderer.setClearColor(0x0b1220, 1);
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      Math.min(90, Math.max(35, fovDeg)),
      1,
      0.1,
      1100,
    );
    const geometry = new THREE.SphereGeometry(500, 60, 40);
    geometry.scale(-1, 1, 1);
    const texture = new THREE.VideoTexture(video);
    texture.colorSpace = THREE.SRGBColorSpace ?? undefined;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    const material = new THREE.MeshBasicMaterial({ map: texture });
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    let yaw = 0;
    let pitch = 0;
    const CLICK_PX = 10;
    let ptrDown = false;
    let dragRotate = false;
    let sx = 0;
    let sy = 0;
    let lastX = 0;
    let lastY = 0;

    const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
    const R = 500;

    const lookTarget = new THREE.Vector3();
    const updateCamera = () => {
      const phi = THREE.MathUtils.degToRad(90) - pitch;
      const theta = yaw;
      lookTarget.set(
        R * Math.sin(phi) * Math.cos(theta),
        R * Math.cos(phi),
        R * Math.sin(phi) * Math.sin(theta),
      );
      camera.lookAt(lookTarget);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      ptrDown = true;
      dragRotate = false;
      sx = lastX = e.clientX;
      sy = lastY = e.clientY;
      renderer.domElement.style.cursor = "grabbing";
      try {
        renderer.domElement.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!ptrDown) return;
      const dx0 = e.clientX - sx;
      const dy0 = e.clientY - sy;
      if (!dragRotate && dx0 * dx0 + dy0 * dy0 > CLICK_PX * CLICK_PX) {
        dragRotate = true;
        lastX = e.clientX;
        lastY = e.clientY;
        return;
      }
      if (!dragRotate) return;
      const mdx = e.clientX - lastX;
      const mdy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      yaw -= mdx * 0.0035;
      pitch += mdy * 0.0035;
      pitch = clamp(pitch, -1.2, 1.2);
    };
    const onPointerUp = () => {
      if (!ptrDown) return;
      ptrDown = false;
      dragRotate = false;
      renderer.domElement.style.cursor = "grab";
    };
    const onPointerCancel = () => {
      if (!ptrDown) return;
      ptrDown = false;
      dragRotate = false;
      renderer.domElement.style.cursor = "grab";
    };

    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.display = "block";
    renderer.domElement.style.cursor = "grab";
    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointermove", onPointerMove);
    renderer.domElement.addEventListener("pointerup", onPointerUp);
    renderer.domElement.addEventListener("pointercancel", onPointerCancel);

    const resize = () => {
      const w = host.clientWidth || 1;
      const h = host.clientHeight || 1;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(host);
    resize();

    const applyFov = (deg: number): void => {
      const next = Math.min(90, Math.max(35, deg));
      if (Math.abs(camera.fov - next) < 0.01) return;
      camera.fov = next;
      camera.updateProjectionMatrix();
    };

    const animate = () => {
      if (destroyed) return;
      applyFov(fovTargetRef.current);
      updateCamera();
      texture.needsUpdate = true;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      destroyed = true;
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointermove", onPointerMove);
      renderer.domElement.removeEventListener("pointerup", onPointerUp);
      renderer.domElement.removeEventListener("pointercancel", onPointerCancel);
      try {
        host.removeChild(renderer.domElement);
      } catch {
        /* ignore */
      }
      texture.dispose?.();
      material.dispose?.();
      geometry.dispose?.();
      renderer.dispose?.();
    };
  }, [enabled, videoRef]);

  if (!enabled) return null;

  return (
    <div
      ref={hostRef}
      className="fb-playback__pano360"
      aria-label="360° immersive view"
    />
  );
}


